import PopupBasic from 'src/game/components/popups/PopupBasic';
import i18n from 'src/lib/i18n/i18n';
import uiConfig from 'src/lib/ui/config';
import ButtonScaleViewWithText from 'src/lib/ui/components/ButtonScaleViewWithText';
import bitmapFonts from 'src/lib/bitmapFonts';
import View from '@play-co/timestep-core/lib/ui/View';
import sounds from 'src/lib/sounds';
import ScrollBasic from '../components/shared/ScrollBasic';
import { ImageScaleView, ImageView } from '@play-co/timestep-core/ui';
import LangBitmapFontTextView from 'src/lib/ui/components/LangBitmapFontTextView';
import { openPopupPromise } from 'src/lib/popups/popupOpenClose';
import StateObserver from 'src/StateObserver';
import GCInstant from '@play-co/gcinstant';
import { Gifts } from 'src/replicant/buruburu/gifts';
import { Avatar } from '../components/shared/Avatar';
import { createEmitter } from 'src/lib/Emitter';
import { chunk } from 'src/lib/utils';
import { ActionSequence } from 'src/lib/ActionSequence';
import { socialSequences } from '../social/social.sequence';
import { sendGiftU2U, sendMassGiftsShare } from './social';
import { captureGenericError } from 'src/lib/sentry';
import { GiftsSocialFriend, sortFriendsForGifting } from './helpers';
import { Feature } from 'src/replicant/analytics';
import { trackGiftCollect } from './analytics';
import { getDynamicReceiverBucket } from 'src/replicant/getters/ab';
import ab from 'src/replicant/ruleset/ab';
import { Social } from 'src/replicant/social';

type CloseHandler = () => void;

type FriendsListItem = GiftsSocialFriend & { type: 'friend' | 'invite' };

/**
 * Gifts shop popup
 */
export default class PopupGiftsList extends PopupBasic {
  private scroll: ScrollBasic<FriendsListItem>;
  private close: CloseHandler;
  private collectAndSendAllButton: ButtonScaleViewWithText;

  /**
   * Popup constructor
   */
  constructor(opts: { superview: View; close: CloseHandler }) {
    super({
      superview: opts.superview,
      close: () => opts.close(),
      zIndex: 10000,
      width: uiConfig.popups.buruburu.width,
      height: 820,
      darkerBg: true,
      titleIcon: { image: 'assets/ui/gifts/icons/gifts_popup_icon.png' },
      tracking: { feature: 'gifts', $subFeature: 'gifts-list-popup' },
    });

    this.close = opts.close;

    this.buttonClose.removeFromSuperview();

    this.message.updateOpts({
      size: 26,
      x: 108,
      y: 71,
      width: 403,
      height: 90,
      leading: 0,
    });

    new ImageView({
      tag: 'energy',
      superview: this.box,
      image: 'assets/ui/shop/icons/energy_large.png',
      width: 78,
      height: 91,
      x: 60,
      y: 88,
      scale: 0.6,
    });

    new View({
      superview: this.box,
      backgroundColor: '#FDC381',
      width: 562,
      height: 4,
      x: 7,
      y: 161,
    });

    this.scroll = new ScrollBasic<FriendsListItem>({
      superview: this.box,
      createItem: this.createItem.bind(this),
      showBg: true,
      firstItemOffset: 5,
      margin: 7,
      rect: {
        x: (this.box.style.width - 530) * 0.5,
        y: 175,
        width: 530,
        height: 499,
      },
    });

    new ButtonScaleViewWithText({
      ...uiConfig.buttons.cancel,
      superview: this.box,
      labelOffsetY: 0,
      labelOffsetX: -9,
      fontSize: 50,
      font: bitmapFonts('Body'),
      x: this.box.style.width / 2 - 255 / 2,
      y: 742,
      width: 228,
      height: 94.7,
      centerOnOrigin: true,
      localeText: () => i18n('basic.close'),
      onClick: async () => {
        sounds.sfx.play('cancel');
        return opts.close();
      },
      tracking: { feature: 'gifts', $subFeature: 'gifts-list-popup-close' },
    });

    this.collectAndSendAllButton = new ButtonScaleViewWithText({
      ...uiConfig.buttons.secondary,
      superview: this.box,
      labelOffsetY: 0,
      labelOffsetX: -9,
      fontSize: 50,
      font: bitmapFonts('Body'),
      x: this.box.style.width / 2 + 255 / 2,
      y: 742,
      width: 228,
      height: 94.7,
      centerOnOrigin: true,
      localeText: () => i18n('gifts.popupGiftsList.collectAndSendAllBtn'),
      onClick: async () => this.collectAndSendAll(this.collectAndSendAllButton),
      tracking: { feature: 'gifts', $subFeature: 'gifts-list-popup-close' },
    });

    // Update list on state changes
    createEmitter(this.box, ({ user }) => ({
      received: user.gifts.received,
      sent: user.gifts.sent,
    })).addListener(() => this.refresh());
  }

  /**
   * Create view item
   */
  private createItem(superview: View, _: number, item: FriendsListItem) {
    const view = new View({
      tag: 'item',
      superview,
      width: 485,
      height: 95,
    });

    new ImageScaleView({
      tag: 'frame',
      superview: view,
      image: 'assets/ui/shared/lossless/scroller_panel_blue.png',
      scaleMethod: '9slice',
      width: 485,
      height: 95,
      x: (superview.style.width - 485) * 0.5,
      sourceSlices: {
        vertical: { top: 35, bottom: 35 },
        horizontal: { left: 35, right: 35 },
      },
    });

    // Render alternative content for invite view
    if (item.type === 'invite') {
      return this.createInviteView(view);
    }

    new Avatar({
      superview: view,
      x: 36,
      y: 10,
      width: 68,
      height: 68,
      centerOnOrigin: false,
    }).updateImage(item.photo);

    new LangBitmapFontTextView({
      tag: 'friend-name',
      superview: view,
      align: 'left',
      verticalAlign: 'center',
      color: '#9A7171',
      wordWrap: false,
      x: 112,
      y: 10,
      size: 28,
      width: 225,
      height: 35,
      font: bitmapFonts('Body'),
      localeText: () => item.name,
      letterSpacing: -2,
    });

    new LangBitmapFontTextView({
      tag: 'activity-text',
      superview: view,
      align: 'left',
      verticalAlign: 'center',
      color: '#7191AB',
      wordWrap: false,
      x: 115,
      y: 44,
      size: 16,
      width: 225,
      height: 32,
      font: bitmapFonts('Body'),
      localeText: () => this.getActivityText(item),
      letterSpacing: -2,
    });

    // Add action button
    // Button change function and look depends on item state
    const button = this.createActionButton(item);

    view.addSubview(button);

    return view;
  }

  /**
   * Create view for invite item
   */
  private createInviteView(view: View) {
    new ImageView({
      tag: 'invite-icon',
      superview: view,
      image: 'assets/ui/buruburu/friend_referral_icon.png',
      width: 70,
      height: 56,
      x: 34,
      y: 13,
      scale: 1.1,
    });

    new LangBitmapFontTextView({
      tag: 'invite-title',
      superview: view,
      align: 'left',
      verticalAlign: 'center',
      color: '#7192AB',
      wordWrap: false,
      x: 118,
      y: 20,
      size: 19,
      width: 245,
      height: 32,
      font: bitmapFonts('Body'),
      localeText: () => i18n('gifts.popupGiftsList.inviteTitle'),
      letterSpacing: -2,
    });

    new LangBitmapFontTextView({
      tag: 'description',
      superview: view,
      align: 'left',
      verticalAlign: 'center',
      color: '#7191AB',
      wordWrap: false,
      x: 118,
      y: 46,
      size: 13,
      width: 225,
      height: 32,
      font: bitmapFonts('Body'),
      localeText: () =>
        i18n('gifts.popupGiftsList.inviteDescription', {
          reward: Social.getters(StateObserver.getState().user).getInviteReward(
            Feature.INVITE.INVITE_GIFTS,
          ).value,
        }),
      letterSpacing: -2,
    });

    const inviteBtn = new ButtonScaleViewWithText({
      tag: 'invite-button',
      superview: view,
      ...uiConfig.buttons.secondary,
      font: bitmapFonts('Body'),
      fontSize: 23,
      labelOffsetX: 0,
      labelOffsetY: -2,
      x: 363,
      y: 12,
      width: 124,
      height: 66,
      lineSpacing: -14,
      localeText: () => i18n('gifts.popupGiftsList.inviteBtn'),
      onClick: async () => {
        sounds.playSound('ok');
        this.close();
        await this.inviteFriends();
      },
      tracking: {
        feature: 'gifts',
        $subFeature: 'gifts-invite-button',
      },
    });

    return view;
  }

  /**
   * Get collect and send button style
   */
  private makeCollectAndSendButton(friend: FriendsListItem) {
    const button = new ButtonScaleViewWithText({
      tag: 'collect-and-send-button',
      ...uiConfig.buttons.secondary,
      font: bitmapFonts('Body'),
      fontSize: 23,
      labelOffsetX: 0,
      labelOffsetY: -2,
      x: 348,
      y: 12,
      width: 142,
      height: 66,
      lineSpacing: -14,
      localeText: () => i18n('gifts.popupGiftsList.collectAndSentGiftBtn'),
      onClick: async () => {
        sounds.playSound('ok');
        void this.collectAndSendGift(button, friend);
      },
      tracking: {
        feature: 'gifts',
        $subFeature: 'gifts-collect-and-send-button',
      },
    });

    return button;
  }

  /**
   * Get collect button style
   */
  private makeCollectButton(friend: FriendsListItem) {
    const button = new ButtonScaleViewWithText({
      tag: 'collect-button',
      ...uiConfig.buttons.secondary,
      font: bitmapFonts('Body'),
      fontSize: 23,
      labelOffsetX: -3,
      labelOffsetY: -2,
      x: 348,
      y: 12,
      width: 142,
      height: 66,
      lineSpacing: -14,
      localeText: () => i18n('gifts.popupGiftsList.collectGiftBtn'),
      onClick: async () => {
        sounds.playSound('ok');
        void this.collectGift(button, friend);
      },
      tracking: {
        feature: 'gifts',
        $subFeature: 'gifts-collect-button',
      },
    });

    return button;
  }

  /**
   * Get send button style
   */
  private makeSendButton(friend: FriendsListItem) {
    const button = new ButtonScaleViewWithText({
      tag: 'send-button',
      ...uiConfig.buttons.secondary,
      font: bitmapFonts('Body'),
      fontSize: 23,
      labelOffsetX: -3,
      labelOffsetY: -2,
      x: 363,
      y: 12,
      width: 124,
      height: 66,
      lineSpacing: -14,
      localeText: () => i18n('gifts.popupGiftsList.sendGiftBtn'),
      onClick: async () => {
        sounds.playSound('ok');
        await this.sendGift(button, friend, Feature.GIFTS.SEND_ENERGY);
      },
      tracking: {
        feature: 'gifts',
        $subFeature: 'gifts-send-button',
      },
    });

    return button;
  }

  /**
   * Already sent disabled button
   */
  private makeSentButton() {
    const button = new ButtonScaleViewWithText({
      tag: 'send-button',
      ...uiConfig.buttons.secondary,
      font: bitmapFonts('Body'),
      fontSize: 23,
      labelOffsetX: -3,
      labelOffsetY: -2,
      x: 363,
      y: 12,
      width: 124,
      height: 66,
      lineSpacing: -14,
      localeText: () => i18n('gifts.popupGiftsList.sentGiftBtn'),
      onClick: async () => {},
      tracking: {
        feature: 'gifts',
        $subFeature: 'gifts-sent-button',
      },
    });

    button.setDisabled(true);

    return button;
  }

  // -----------------------------------------------------------------
  // Business logic below
  // -----------------------------------------------------------------

  /**
   * On dialog init
   */
  public init() {
    super.init({
      title: i18n('gifts.popupGiftsList.title'),
      message: i18n('gifts.popupGiftsList.message'),
    });

    // Update list UI
    this.refresh();
  }

  /**
   * Refresh list UI
   */
  private refresh() {
    // Set state of collect all & send all button
    this.collectAndSendAllButton.setDisabled(!this.canCollectAll());

    // Get list of friends
    const list = this.getFriendsList();

    // Add invite item to the and of scroll
    list.push({ type: 'invite' });

    // Set friends list to scroll
    this.scroll.setItems(list);
  }

  /**
   * Actions button depend on state of target
   */
  private createActionButton(friend: FriendsListItem) {
    // If we can collect and send
    if (friend.hasGift && friend.canSend) {
      return this.makeCollectAndSendButton(friend);
    }

    // If we can collect
    if (friend.hasGift) {
      return this.makeCollectButton(friend);
    }

    // If we can send
    if (friend.canSend) {
      return this.makeSendButton(friend);
    }

    // Disable send button if we can't send and can't collect
    return this.makeSentButton();
  }

  /**
   * Get activity text
   */
  private getActivityText(friend: FriendsListItem) {
    // If we can collect and send
    if (friend.hasGift && friend.canSend) {
      return i18n('gifts.popupGiftsList.activityText.sentYouEnergy');
    }

    // If we can collect
    if (friend.hasGift) {
      return i18n('gifts.popupGiftsList.activityText.sentYouEnergy');
    }

    // If we can send
    if (friend.canSend) {
      return i18n('gifts.popupGiftsList.activityText.letsSendEnergy');
    }

    // Default
    return '';
  }

  /**
   * canSendGift helper
   */
  private canSend(friendId: string) {
    const now = StateObserver.now();
    const state = StateObserver.getState().user;
    const { canSendGift } = Gifts.getters(state);
    return canSendGift(friendId, now);
  }

  /**
   * hasGift helper
   */
  private hasGift(friendId: string) {
    const state = StateObserver.getState().user;
    const { hasGift } = Gifts.getters(state);
    return hasGift(friendId);
  }

  /**
   * canCollect helper
   */
  private getEnergyRewardsTotal() {
    const now = StateObserver.now();
    const state = StateObserver.getState().user;
    return Gifts.getters(state).getEnergyRewardsTotal(now);
  }

  /**
   * Can collect all helper
   */
  private canCollectAll() {
    const friends = this.getFriendsList();

    const someCanCollect = friends.some((friend) => friend.canCollect);
    const someCanSend = friends.some((friend) => friend.canSend);

    return someCanCollect || someCanSend;
  }

  /**
   * Return list of friends
   */
  private getFriendsList(): FriendsListItem[] {
    const now = StateObserver.now();
    const { user } = StateObserver.getState();
    const { canSendGift, canCollect, hasGift } = Gifts.getters(user);

    const list: FriendsListItem[] = GCInstant.friends.map((friend) => {
      return {
        type: 'friend',
        id: friend.id,
        name: friend.name,
        photo: friend.photo,
        hasGift: hasGift(friend.id),
        canSend: canSendGift(friend.id, now),
        canCollect: canCollect(friend.id, now),
      };
    });

    // Sort friends
    return sortFriendsForGifting(list) as FriendsListItem[];
  }

  /**
   * Send gift to user
   */
  private async sendGift(
    button: ButtonScaleViewWithText,
    friend: FriendsListItem,
    subFeature: string,
  ) {
    button.setDisabled(true);

    // Show U2U confirmation popup
    const updateAsyncConfirmation = await openPopupPromise(
      'popupGiftsConfirmation',
      {
        avatar: friend.photo,
        playerName: friend.name,
      },
    );

    // Send update async
    if (updateAsyncConfirmation) {
      try {
        await sendGiftU2U(friend.id, subFeature);
        await StateObserver.invoke.sendGiftsMessage(friend.id);
      } catch (e) {
        captureGenericError('Failed to send gift energy u2u message', e);
      }
    }

    // Update button state
    button.setDisabled(!this.canSend(friend.id));
  }

  /**
   * Collect gift
   */
  private async collectGift(
    button: ButtonScaleViewWithText,
    friend: FriendsListItem,
  ) {
    button.setDisabled(true);

    // Friend has gift but you can't collect it
    if (!friend.canCollect) {
      await openPopupPromise('popupModalWhite', {
        title: i18n('gifts.popupCollectError.title'),
        message: i18n('gifts.popupCollectError.message', {
          max: Gifts.ruleset.maxClaimEnergy,
        }),
        tracking: {
          feature: 'gifts',
          $subFeature: 'gifts-cant-collect',
        },
      });

      return;
    }

    // Show reward confirmation popup
    const collectConfirmation = await openPopupPromise('popupGiftReward', {
      amount: Gifts.ruleset.energyGiftAmount,
    });

    // Give reward
    if (collectConfirmation) {
      const analytics = await StateObserver.invoke.consumeGift(friend.id);
      // Send client side analytics
      trackGiftCollect(analytics);
    }

    // Update button state
    button.setDisabled(!this.hasGift(friend.id));
  }

  /**
   * Collect and send gift
   */
  private async collectAndSendGift(
    button: ButtonScaleViewWithText,
    friend: FriendsListItem,
  ) {
    button.setDisabled(true);

    // Run collect logic
    await this.collectGift(button, friend);

    // Run send logic
    await this.sendGift(button, friend, Feature.GIFTS.SEND_ENERGY_AND_COLLECT);

    // Update UI state
    this.refresh();
  }

  /**
   * Collect and send gifts to all available user
   */
  private async collectAndSendAll(button: ButtonScaleViewWithText) {
    // Find friends available for messages
    const friends = this.getFriendsList().filter((friend) => friend.canSend);

    button.setDisabled(true);

    // Show reward confirmation popup
    const availableEnergy = this.getEnergyRewardsTotal();

    // We may already collect all energy,
    // in this case it will just send messages and skip reward popup
    if (availableEnergy) {
      const collectConfirmation = await openPopupPromise('popupGiftReward', {
        amount: availableEnergy,
      });

      // Give reward
      if (collectConfirmation) {
        const analytics = await StateObserver.invoke.consumeAllGifts();
        // Send analytics
        analytics.forEach((data) => trackGiftCollect(data));
      }
    }

    // Spit friends by groups of ten
    const groups = chunk(friends, 10);

    // Loop over each group
    for (let index = 0; index < groups.length; index++) {
      const group = groups[index];
      const avatars = group.map((friend) => friend.photo);
      const ids = group.map((friend) => friend.id);

      // Show U2U confirmation popup
      const updateAsyncConfirmation = await openPopupPromise(
        'popupGiftsConfirmationMulti',
        { avatars },
      );

      // User canceled sending
      if (!updateAsyncConfirmation) break;

      try {
        // Open mass share dialog
        const sent = await sendMassGiftsShare(ids);

        // Send gifts, to actual shared users
        this.sendGiftsReplicantMessage(sent);
      } catch (e) {
        captureGenericError('Failed to send gift energy share message', e);
      }
    }

    // Update UI state
    this.refresh();
  }

  /**
   * sendGiftsMessage action warped to catch
   */
  private sendGiftsReplicantMessage(friendsIds: string[]) {
    friendsIds.forEach(async (id) => {
      try {
        await StateObserver.invoke.sendGiftsMessage(id);
      } catch (e) {
        captureGenericError(
          `Failed to send gift energy share message to ${id}`,
          e,
        );
      }
    });
  }

  /**
   * Invite friends
   */
  private async inviteFriends() {
    await ActionSequence.start(
      socialSequences.invite(Feature.INVITE.INVITE_GIFTS),
      'Invite',
    )
      // Ignore errors, they'll be reported by ActionSequence.
      .catch((e) => void e);
  }
}
